import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { LoadingIndicator } from '@/components/base/loading-indicator/loading-indicator';
import { useCaptureParticipantUtmFromSearch } from '@/hooks/use-capture-participant-utm-from-search';
import { usePersistProjectUtmFromSearch } from '@/hooks/use-persist-project-utm-from-search';
import { RecaptchaScript } from '@/hooks/use-recaptcha';
import { redirectResearchersToDashboard } from '@/routes/participants/-utils/participant-route-guards';
import { QUESTION_TYPES } from '@/routes/participants/_authenticated/projects/$projectId/-lib/screener-types';
import { projectSearchSchema } from '@/routes/participants/_authenticated/projects/$projectId/-shared';
import { getPublicEnv } from '@/utils/session/get-public-env';

import { mergePublicApplyProfileQuestions } from './-public-apply/merge-profile-questions';
import { PublicApplyForm } from './-public-apply/public-apply-form';
import {
  publicB2bQuestionsQueryOptions,
  publicProfileQuestionsQueryOptions,
  publicScreenerQuestionsQueryOptions,
  publicProjectDetailsQueryOptions,
} from './-public-apply/queries';
import { useBroadcastContentHeight } from './-public-apply/use-broadcast-content-height';

export const Route = createFileRoute(
  '/participants/_public/projects/$projectId/public-apply',
)({
  validateSearch: projectSearchSchema,

  beforeLoad: async ({ params, search, context }) => {
    const { user, env, queryClient } = context;

    const publicEnv = await getPublicEnv();

    if (user) {
      redirectResearchersToDashboard(user, env);

      // A signed-in participant belongs on the real apply flow: it knows their
      // eligibility, resumes their draft and runs the pre-submission steps.
      throw redirect({
        to: '/participants/projects/$projectId/apply',
        params: { projectId: params.projectId },
        search,
      });
    }

    const invite = {
      projectId: params.projectId,
      inviteCode: search.inviteCode ?? search.invite,
      inviteId: search.inviteId,
    };

    // Details first: the gate is a field on the project itself, and a project
    // that isn't opted in never pays for the screener and profile-question
    // fetches.
    const details = await queryClient.ensureQueryData(
      publicProjectDetailsQueryOptions(invite),
    );

    // Opt-in is per project: `publicApplicationsEnabled` is set manually in
    // the database, and absent means off — every project stays gated until
    // someone deliberately flips it. When off, behave as though the page
    // doesn't exist: the gated apply route sends a logged-out visitor to
    // login with a returnTo, which is exactly what clicking "apply" does
    // today.
    if (details.project.publicApplicationsEnabled !== true) {
      throw redirect({
        to: '/participants/projects/$projectId/apply',
        params: { projectId: params.projectId },
        search,
      });
    }

    const [screener] = await Promise.all([
      queryClient.ensureQueryData(publicScreenerQuestionsQueryOptions(invite)),
      queryClient.ensureQueryData(
        publicProfileQuestionsQueryOptions({ projectId: params.projectId }),
      ),
      queryClient.ensureQueryData(
        publicB2bQuestionsQueryOptions({ projectId: params.projectId }),
      ),
    ]);

    // A file-upload answer cannot be made without an account: the S3 key
    // embeds the participant's user id and every upload endpoint is
    // role-gated. Send those screeners to signup rather than let someone fill
    // in half a form they can't finish.
    const hasFileUpload = screener.questions?.some(
      (question) => question.questionType === QUESTION_TYPES.FILE_UPLOAD,
    );

    if (hasFileUpload) {
      throw redirect({
        to: '/participants/signup',
        search: {
          returnTo: `/participants/projects/${params.projectId}/apply`,
        },
      });
    }

    return { publicEnv };
  },

  component: PublicApplyPage,
});

function PublicApplyPage() {
  const { projectId } = Route.useParams();
  const { publicEnv } = Route.useRouteContext();
  const search = Route.useSearch();

  // Campaign attribution, exactly as the signed-in flow's project detail page
  // does it. It matters more here: nothing inside the app links to this page,
  // so the URL is always an external one — an ad, an email, a social post —
  // and this is the only chance to read its `utm_*` params before the
  // application is submitted.
  useCaptureParticipantUtmFromSearch(search);
  usePersistProjectUtmFromSearch(projectId, search);

  // When the page is embedded in an iframe, tell the host how tall the
  // content is so it can size the frame instead of hardcoding a height.
  useBroadcastContentHeight();

  const invite = {
    projectId,
    inviteCode: search.inviteCode ?? search.invite,
    inviteId: search.inviteId,
  };

  const { data: details } = useQuery(publicProjectDetailsQueryOptions(invite));
  const { data: screener } = useQuery(
    publicScreenerQuestionsQueryOptions(invite),
  );
  const { data: profile } = useQuery(
    publicProfileQuestionsQueryOptions({ projectId }),
  );
  const { data: b2b } = useQuery(publicB2bQuestionsQueryOptions({ projectId }));

  if (!details || !screener || !profile || !b2b) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-16">
          <LoadingIndicator />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PublicApplyForm
        projectId={projectId}
        details={details}
        screenerQuestions={screener.questions ?? []}
        // Registry profile questions first, then the B2B employment questions
        // (empty for non-B2B projects) — one list, so buffering, the question
        // fingerprint and the progress counter treat them alike. A key both
        // lists carry is asked once.
        profileQuestions={mergePublicApplyProfileQuestions(
          profile.questions ?? [],
          b2b.questions ?? [],
        )}
        nda={screener.nda}
        inviteCode={invite.inviteCode}
        inviteId={invite.inviteId}
        websiteUrl={publicEnv.WEBSITE_URL}
        search={search}
      />
    </PageContainer>
  );
}

function PageContainer({ children }: { children: React.ReactNode }) {
  return (
    // The page is the question card and nothing else — no nav, no project
    // panel — so the container is sized to the card rather than to the app's
    // full-width shell.
    <div className="mx-auto max-w-2xl px-4 pt-6 pb-18 md:px-8">
      {/*
       * The account step scores a v3 token before it will send a code or file
       * an application, and the v2 checkbox it falls back to renders through
       * the same `grecaptcha` object — neither exists unless this script is on
       * the page. It sits here, above the loading branch, so it loads while
       * the applicant is still answering questions rather than at the moment
       * the account step asks for a token.
       */}
      <RecaptchaScript />
      {children}
    </div>
  );
}
